import {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useEffect,
  useMemo,
} from 'react'
import PropTypes from 'prop-types'
import { useFilters } from './FilterContext'
import { useData } from './DataContext'

const ViewContext = createContext()

const initialState = {
  activeView: 'map',
  mapState: {
    center: [0.7, 32.9], // Kayunga District center
    zoom: 10,
    selectedFacilityId: null,
    showClusters: true,
  },
  gridState: {
    currentPage: 1,
    itemsPerPage: 12,
    sortBy: 'name',
    sortOrder: 'asc',
  },
  chartsState: {
    selectedMetric: 'facility_level',
    chartType: 'pie',
  },
  comparisonState: {
    compareBy: 'subcounty',
    selectedMetric: 'services',
  },
  showFacilityDetail: false,
  selectedFacilityId: null,
}

function viewReducer(state, action) {
  switch (action.type) {
    case 'SET_ACTIVE_VIEW':
      return {
        ...state,
        activeView: action.view,
      }

    case 'UPDATE_MAP_STATE':
      return {
        ...state,
        mapState: {
          ...state.mapState,
          ...action.updates,
        },
      }

    case 'UPDATE_GRID_STATE':
      return {
        ...state,
        gridState: {
          ...state.gridState,
          ...action.updates,
        },
      }

    case 'UPDATE_CHARTS_STATE':
      return {
        ...state,
        chartsState: {
          ...state.chartsState,
          ...action.updates,
        },
      }

    case 'UPDATE_COMPARISON_STATE':
      return {
        ...state,
        comparisonState: {
          ...state.comparisonState,
          ...action.updates,
        },
      }

    case 'SHOW_FACILITY_DETAIL':
      return {
        ...state,
        showFacilityDetail: true,
        selectedFacilityId: action.facilityId,
      }

    case 'HIDE_FACILITY_DETAIL':
      return {
        ...state,
        showFacilityDetail: false,
        selectedFacilityId: null,
      }

    default:
      return state
  }
}

export function ViewProvider({ children }) {
  const [state, dispatch] = useReducer(viewReducer, initialState)
  const { config } = useData()
  const { activeCategory } = useFilters()

  // Get current category configuration and available views
  const currentCategoryConfig = config?.facility_types?.find(
    type => type.id === activeCategory
  )
  const availableViews = useMemo(
    () =>
      currentCategoryConfig?.available_views || [
        'map',
        'grid',
        'charts',
        'comparison',
      ],
    [currentCategoryConfig]
  )

  const defaultView = currentCategoryConfig?.default_view || 'map'

  // Auto-switch to default view when category changes or current view is not available
  useEffect(() => {
    if (currentCategoryConfig && !availableViews.includes(state.activeView)) {
      dispatch({ type: 'SET_ACTIVE_VIEW', view: defaultView })
    }
  }, [
    activeCategory,
    availableViews,
    defaultView,
    state.activeView,
    currentCategoryConfig,
  ])

  // Set default sort for education category to 'details'
  useEffect(() => {
    if (activeCategory === 'education' && state.gridState.sortBy === 'name') {
      dispatch({
        type: 'UPDATE_GRID_STATE',
        updates: {
          sortBy: 'details',
          sortOrder: 'asc',
          currentPage: 1,
        },
      })
    } else if (
      activeCategory === 'health' &&
      state.gridState.sortBy === 'details'
    ) {
      // Reset to name sort when switching from education to health
      dispatch({
        type: 'UPDATE_GRID_STATE',
        updates: {
          sortBy: 'name',
          sortOrder: 'asc',
          currentPage: 1,
        },
      })
    }
  }, [activeCategory, state.gridState.sortBy])

  const setActiveView = useCallback(
    view => {
      // Only allow switching to available views
      if (availableViews.includes(view)) {
        dispatch({ type: 'SET_ACTIVE_VIEW', view })
      }
    },
    [availableViews]
  )

  const updateMapState = useCallback(updates => {
    dispatch({ type: 'UPDATE_MAP_STATE', updates })
  }, [])

  const updateGridState = useCallback(updates => {
    dispatch({ type: 'UPDATE_GRID_STATE', updates })
  }, [])

  const updateChartsState = useCallback(updates => {
    dispatch({ type: 'UPDATE_CHARTS_STATE', updates })
  }, [])

  const updateComparisonState = useCallback(updates => {
    dispatch({ type: 'UPDATE_COMPARISON_STATE', updates })
  }, [])

  const showFacilityDetailModal = useCallback(facilityId => {
    dispatch({ type: 'SHOW_FACILITY_DETAIL', facilityId })
  }, [])

  const hideFacilityDetail = useCallback(() => {
    dispatch({ type: 'HIDE_FACILITY_DETAIL' })
  }, [])

  const value = {
    ...state,
    availableViews,
    setActiveView,
    updateMapState,
    updateGridState,
    updateChartsState,
    updateComparisonState,
    showFacilityDetailModal,
    hideFacilityDetail,
  }

  return <ViewContext.Provider value={value}>{children}</ViewContext.Provider>
}

export function useView() {
  const context = useContext(ViewContext)
  if (!context) {
    throw new Error('useView must be used within a ViewProvider')
  }
  return context
}

ViewProvider.propTypes = {
  children: PropTypes.node.isRequired,
}
